/**
 * Mirror-outline colour for mirrored activities/milestones (MapActivityMarker
 * `strokeColor` / MapMilestoneMarker `milestoneStrokeColor`): the source project's
 * slot in `colorPaletteMirroredProjects`, indexed by its position among the map's
 * referenced projects, falling back to black. In status view the SVG drops the
 * inline stroke and the CSS black default applies, so the resolver forces black
 * when `statusView` is set.
 *
 * `allReferencedProjects` rebuilds a Map over every mirror on each call, so we
 * resolve the source-project → index lookup ONCE per frame and hand back a cheap
 * closure — the painters call it per mirrored item without the O(n²) rescan.
 * MapGroupList outlines its mirrored rows' hexagons with it too.
 */
import { colorPaletteMirroredProjects } from '@/utils/helpers/colors';
import type { SceneContext } from '../../engine/SceneContext';

export type MirroredStroke = (
  sourceProjectId: string | undefined,
  statusView: boolean,
) => string;

export function makeMirroredStroke(
  scene: Pick<SceneContext, 'map' | 'project'>,
): MirroredStroke {
  // Templates have no cross-project mirrors, AND `projectStore.project(mapId)`
  // throws for a template id (it isn't in `state.projects`) — which would abort
  // the whole content paint. So never resolve the referenced-project list there;
  // a template's outline resolver is always black.
  if (scene.map.isTemplate) {
    return (): string => '#000000';
  }
  const projects = scene.project.allReferencedProjects(scene.map.mapId);
  const indexById = new Map(
    projects.map((p, i) => [p.source_project_id, i] as const),
  );

  return (sourceProjectId, statusView): string => {
    if (statusView || !sourceProjectId) {
      return '#000000';
    }
    const index = indexById.get(sourceProjectId);
    if (index === undefined) {
      return '#000000';
    }

    return colorPaletteMirroredProjects[
      index % colorPaletteMirroredProjects.length
    ];
  };
}
